import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import api from "../lib/api";
import { Bell, X, Check } from "lucide-react";

export function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = () => api.get("/notifications").then((r) => { setItems(r.data.items); setUnread(r.data.unread); }).catch(() => {});

  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, []);

  const openPanel = async () => {
    setOpen(true);
    if (unread > 0) {
      try { await api.post("/notifications/read-all"); setUnread(0); } catch {}
    }
  };

  return (
    <>
      <button data-testid="notif-bell" onClick={openPanel} className="relative flex items-center justify-center h-10 w-10 rounded-xl bg-white border border-[#E2DFD8] hover:border-[#D94F2B] transition-colors">
        <Bell className="w-4 h-4 text-[#1A1A1A]" />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F26419] text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} className="fixed inset-0 z-[1000] bg-[#1A1A1A]/40 backdrop-blur-sm flex items-start justify-end p-3 sm:p-5">
            <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm bg-[#FDF8F0] rounded-2xl overflow-hidden shadow-2xl" data-testid="notif-panel">
              <div className="flex items-center justify-between h-14 px-4 bg-white border-b border-[#E2DFD8]">
                <div className="font-head font-bold flex items-center gap-2"><Bell className="w-4 h-4 text-[#D94F2B]" /> {t("notif.center")}</div>
                <button data-testid="notif-close" onClick={() => setOpen(false)} className="w-8 h-8 rounded-lg hover:bg-[#F0EBE1] flex items-center justify-center"><X className="w-4 h-4 text-[#75807A]" /></button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto no-scrollbar p-3 space-y-2">
                {items.length === 0 && <p className="text-center text-sm text-[#75807A] py-10">{t("notif.empty")}</p>}
                {items.map((n) => (
                  <div key={n.id} data-testid="notif-item" className="bg-white rounded-xl border border-[#E2DFD8] p-3">
                    <div className="flex items-start gap-2">
                      <span className="w-8 h-8 shrink-0 rounded-lg bg-[#F26419] flex items-center justify-center"><Check className="w-4 h-4 text-[#1A1A1A]" /></span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-[#1A1A1A]">{n.title}</div>
                        <div className="text-xs text-[#3B423E]">{n.body}</div>
                        <div className="text-[10px] text-[#75807A] mt-1">{new Date(n.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
